import { tealiumLogSearchClick } from "ComponentHelpers/SearchboxTealiumHelpers";
import i18next from "i18next";
import url from "url";
import { html } from "lit-html";
import { logOutInline, getRequestUrl } from "Services/common";
import { FLIGHT_SELECT_URL, ROLE_CODES, COMMON_DAYJS_FORMAT, USA_DOLLAR_CODE } from "Services/constants";
import { PUB_SUBS } from "Services/pub-sub-service/PubSub";
import { TestIdDictionary } from "Services/test-ids/TestIdDictionary";
import { createAmericanAirlinesLoginUrl } from "Services/urls";
import SearchRequest from "Shared/models/SearchRequest";
import { useStationSelector } from "./useStationSelector";
import { useNewFlightDateSelector } from "./useNewFlightDateSelector";
import { usePassengerSelector } from "./usePassengerSelector";
import { useEffectWithoutCleanup, useMemo } from "Shared/haunted/CustomHooks";
import { usePromoCodeBox } from "Components/searchbox/searchTab/flights/promo-code-box/usePromoCodeBox";
import { useReduxState } from "Shared/redux/useReduxState";
import { usePreviousSearch } from "./usePreviousSearch";
import { useRadioToggle } from "../../../useRadioToggle";
import dayjs from "dayjs";
import { useWindowSize } from "Shared/haunted/useWindowSize";
import { iconButton } from "Components/iconButton";
import { DynamicSettings } from "Components/admin/DynamicSettings";

export interface Props {
	culture: string;
	dynamicSettings: DynamicSettings;
	loaderElemId: string;
}

const REDEMPTION_MILES_ID = "redemptionMiles";

export const useFlightsTab = (props: Props) => {
	const windowSize = useWindowSize();

	const [userInfo] = useReduxState("userInfo");
	const [currentSearch, setCurrentSearch] = useReduxState("currentSearch");
	const [countries] = useReduxState("countries");

	const isChileCompraUser = () => userInfo?.ChileCompra?.IsMember;
	const isCug2User = () => Boolean(userInfo?.OrganizationName);
	const isPeruCompraUser = () => userInfo?.PeruCompra?.IsMember || userInfo?.PeruCompra?.IsAdmin;

	const showRedemptionMiles = () =>
		window.JetSmart?.DynamicSettings?.IsAmericanOn && !isCug2User() && !isChileCompraUser() && !isPeruCompraUser();

	const previousSearch = usePreviousSearch();
	const [navitaireSettings] = useReduxState("navitaireSettings");

	const promoCodeBox = usePromoCodeBox({
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
	});

	const showReloadLink = useMemo(
		() =>
			Boolean(previousSearch.load()) &&
			(!currentSearch.useAmericanMiles || !userInfo?.AmericanAirlines?.IsMember),
		[currentSearch.useAmericanMiles, userInfo?.AmericanAirlines?.IsMember]
	);

	const radioToggle = useRadioToggle({
		isOn: !currentSearch.isOneWay,
		firstOptionText: i18next.t("Solo ida"),
		secondOptionText: i18next.t("Ida y vuelta"),
		onChange: (isOneWay: boolean) => {
			setCurrentSearch({
				...currentSearch,
				isOneWay,
				returnDate: currentSearch.isOneWay !== isOneWay ? undefined : currentSearch.returnDate,
			});
		},
	});

	const startLoading = () => {
		const loaderInstance = window.newRtLoader(`#${props.loaderElemId}`);
		loaderInstance.startLoader();
		return loaderInstance;
	};

	const stopLoading = (loader: LoaderInstance) => {
		if (!loader) return;
		loader.stopLoader();
		loader.destroy();
	};

	const stationSelector = useStationSelector({
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		startLoading,
		stopLoading,
	});

	const passengerSelector = usePassengerSelector({ culture: props.culture });

	const dateSelector = useNewFlightDateSelector({
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		openPassengerSelector: passengerSelector.open,
		startLoading,
		stopLoading,
	});

	const getSearchRequest = () => {
		const cultureElements = props.culture.split("-", 2);
		const countryIndex = 1;
		const rc = cultureElements.length === 2 ? cultureElements[countryIndex] : "";
		const isRedemptionSearch =
			showRedemptionMiles() && currentSearch.useAmericanMiles && userInfo?.AmericanAirlines?.IsMember;

		let req: SearchRequest = {
			o1: currentSearch.originCity.code,
			d1: currentSearch.destinationCity.code,
			dd1: currentSearch.departureDate.format(COMMON_DAYJS_FORMAT),
			ADT: currentSearch.passengers.adults + currentSearch.passengers.teens,
			CHD: currentSearch.passengers.children,
			inl: currentSearch.passengers.infants,
			r: false,
			s: true,
			mon: true,
			cur: !isRedemptionSearch ? navitaireSettings.currency : USA_DOLLAR_CODE,
			culture: props.culture,
			pc: !isRedemptionSearch ? currentSearch.promoCode || "" : "",
			rc,
			selectedPriceOutbound: dateSelector.selectedPriceOutbound?.toString().trim() || "",
			selectedPriceInbound: "",
			isRedemptionFlow: isRedemptionSearch || false,
		};

		if (!currentSearch.isOneWay) {
			req = {
				...req,
				dd2: currentSearch.returnDate.format(COMMON_DAYJS_FORMAT),
				selectedPriceInbound: dateSelector.selectedPriceInbound?.toString().trim() || "",
				r: true,
			};
		}

		return req;
	};

	const redirectToQueueItUrl = (requestUrl: string) => {
		const newLocationUrl = encodeURIComponent(
			url.resolve(window.JetSmart.Settings.BookingUrl, `${FLIGHT_SELECT_URL}${requestUrl}`)
		);

		window.location.href = props.dynamicSettings.QueueItPrefix + newLocationUrl;
	};

	const redirectToRegularUrl = (requestUrl: string) => {
		location.href = url.resolve(window.JetSmart.Settings.BookingUrl, `${FLIGHT_SELECT_URL}${requestUrl}`);
	};

	const redirectToAmericanAirlinesLogin = async () => {
		if (userInfo?.RoleCode !== ROLE_CODES.WEB_ANONYMOUS) {
			await logOutInline();
		}

		window.location.href = createAmericanAirlinesLoginUrl(props.culture);
		return;
	};

	const savePreviousSearch = () => {
		previousSearch.save({
			departureDate: currentSearch.departureDate.format(COMMON_DAYJS_FORMAT),
			destination: currentSearch.destinationCity.code,
			isOneWay: currentSearch.isOneWay,
			origin: currentSearch.originCity.code,
			passengers: {
				adults: currentSearch.passengers.adults,
				children: currentSearch.passengers.children,
				infants: currentSearch.passengers.infants,
				teens: currentSearch.passengers.teens,
			},
			returnDate: currentSearch.returnDate?.format(COMMON_DAYJS_FORMAT) || "",
		});
	};

	const handleReloadSearch = async (e: MouseEvent) => {
		e.preventDefault();

		const value = previousSearch.load();

		if (!value) return;

		try {
			setCurrentSearch({
				departureDate: dayjs(value.departureDate),
				destinationCity: countries.flatMap((c) => c.cities).find((c) => c.code === value.destination),
				isOneWay: value.isOneWay,
				isReloading: true,
				originCity: countries.flatMap((c) => c.cities).find((c) => c.code === value.origin),
				passengers: {
					adults: value.passengers.adults,
					children: value.passengers.children,
					infants: value.passengers.infants,
					teens: value.passengers.teens,
				},
				useAmericanMiles: currentSearch.useAmericanMiles,
				returnDate: value.returnDate ? dayjs(value.returnDate) : undefined,
				promoCode: currentSearch.promoCode || promoCodeBox.predefinedPromoCode || "",
			});
		} catch {
			// Do nothing
		}
	};

	const handleFormSubmit = async () => {
		if (
			!currentSearch.originCity ||
			!currentSearch.destinationCity ||
			!currentSearch.departureDate ||
			(!currentSearch.isOneWay && !currentSearch.returnDate) ||
			currentSearch.passengers.adults + currentSearch.passengers.teens === 0
		) {
			return;
		}

		if (currentSearch.promoCode && !promoCodeBox.validate()) return;

		tealiumLogSearchClick({
			adults: currentSearch.passengers.adults,
			children: currentSearch.passengers.children,
			culture: props.culture,
			departureDate: currentSearch.departureDate,
			destinationCode: currentSearch.destinationCity.code,
			infants: currentSearch.passengers.infants,
			numberOfJourneys: currentSearch.isOneWay ? 1 : 2,
			market:
				currentSearch.destinationCity.code !== currentSearch.originCity.code
					? "INTER"
					: `DOM-${currentSearch.originCity.code}`,
			originCode: currentSearch.originCity.code,
			preloadedPromoCode: promoCodeBox.predefinedPromoCode,
			promoCode: currentSearch.promoCode,
			returnDate: currentSearch.returnDate,
			teens: currentSearch.passengers.teens,
		});

		if (currentSearch.promoCode && currentSearch.useAmericanMiles && !userInfo?.AmericanAirlines?.IsMember) {
			PUB_SUBS.RedemptionNoPromoCodeModalOpened.publish({});
			return;
		}

		if (currentSearch.useAmericanMiles && !userInfo?.AmericanAirlines?.IsMember) {
			await redirectToAmericanAirlinesLogin();
			return;
		}

		startLoading();

		savePreviousSearch();

		const request = getSearchRequest();
		const requestUrl = getRequestUrl(request);

		if (props.dynamicSettings.IsQueueItOn) {
			redirectToQueueItUrl(requestUrl);
		} else {
			redirectToRegularUrl(requestUrl);
		}
	};

	const button = iconButton({
		dataTestId: TestIdDictionary.SubmitSearchButton,
		isAction: true,
		icon: html`<i class="jsh-circle-chevron-right1 text-base"></i>`,
		isDisabled:
			!currentSearch.originCity ||
			!currentSearch.destinationCity ||
			!currentSearch.departureDate ||
			(!currentSearch.isOneWay && !currentSearch.returnDate) ||
			currentSearch.passengers.adults + currentSearch.passengers.teens === 0,
		label: i18next.t("search-box-submit"),
		onClick: handleFormSubmit,
	});

	useEffectWithoutCleanup(() => {
		if (userInfo?.RoleCode && !!currentSearch?.useAmericanMiles !== !!userInfo.AmericanAirlines?.IsMember) {
			setCurrentSearch({ ...currentSearch, useAmericanMiles: userInfo?.AmericanAirlines?.IsMember });
		}
	}, [userInfo?.RoleCode, currentSearch?.promoCode]);

	const reloadLinkTemplate = () =>
		showReloadLink
			? html`
					<div class="m-0 flex items-center justify-center px-0 pb-0 pt-2 text-center">
						<span
							class="cursor-pointer text-sm font-bold leading-none text-n-blue underline hover:text-brand-primary"
							@click=${handleReloadSearch}
						>
							&#8635;&nbsp;${i18next.t("Rearmar mi última búsqueda")}
						</span>
					</div>
			  `
			: html``;

	const redemptionMilesCheckboxTemplate = () =>
		showRedemptionMiles()
			? html`
					<div class="dg-redemption-checkbox">
						<input
							type="checkbox"
							id=${REDEMPTION_MILES_ID}
							autocomplete="off"
							?checked=${currentSearch.useAmericanMiles}
							@click=${() =>
								setCurrentSearch({
									...currentSearch,
									useAmericanMiles: !currentSearch.useAmericanMiles,
								})}
						/>
						<label class="ml-2 text-n-blue" for=${REDEMPTION_MILES_ID}
							>${i18next.t("Use AAdvantage® miles")}</label
						>
					</div>
			  `
			: html``;

	const mobileExtraFieldsTemplate = () =>
		currentSearch.originCity && currentSearch.destinationCity
			? html` ${dateSelector.htmlTemplate()}${passengerSelector.htmlTemplate()}${promoCodeBox.htmlTemplate()} `
			: "";

	const mobileFlightsTabTemplate = () => html`
		<div class="flex flex-col gap-4">
			<div class="mt-6 grid grid-cols-2 gap-4">
				<div class="col-span-2">${radioToggle.htmlTemplate()}</div>
				<div class="col-span-2">${stationSelector.htmlTemplate()}</div>
				${mobileExtraFieldsTemplate()}
			</div>
			${redemptionMilesCheckboxTemplate()}
			<div class="flex flex-col gap-4">${reloadLinkTemplate()}${button.htmlTemplate()}</div>
		</div>
	`;

	const desktopFlightsTabTemplate = () => html`
		<div class="mt-4 flex flex-col gap-4">
			<div class="mt-2">${radioToggle.htmlTemplate()}</div>
			<div class="grid w-full grid-cols-[2fr_1fr_1fr_1fr] gap-4">
				${stationSelector.htmlTemplate()} ${dateSelector.htmlTemplate()} ${passengerSelector.htmlTemplate()}
			</div>
			${redemptionMilesCheckboxTemplate()}
			<div class="flex items-center justify-between">
				${reloadLinkTemplate()}
				<div class="flex items-center gap-4">${promoCodeBox.htmlTemplate()}${button.htmlTemplate()}</div>
			</div>
		</div>
	`;

	const htmlTemplate = () =>
		windowSize.size === "XS" || windowSize.size === "SM"
			? html`<div>${mobileFlightsTabTemplate()}</div>`
			: html`<div>${desktopFlightsTabTemplate()}</div> `;

	return { htmlTemplate };
};
